import { FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TooltipRefProps } from 'react-tooltip';
import { Title } from 'components/ModalDialog/style';
import { SUPPORTED_FEATURES_MAPPER } from 'modules/core/constants/mappers/supportedFeatures.mapper';
import { useIsAllyLender } from 'modules/core/hooks/useIsAllyLender';
import { getDisabledFeaturesList } from 'store/selectors/ui.selector';
import { getUser } from 'store/selectors/auth.selector';

import {
  ButtonUndo,
  ButtonUndoBlock,
  ButtonUndoLabel,
  ButtonViewWorksheet,
  ButtonWorksheetBlock,
  ButtonContainerBlock,
  LoanVariablesModalHeaderWrapper,
  ButtonSpinner,
} from './styles';
import { ModalCloseButton } from '../../styles';
import { IAvailableDealStructureStatus } from '../../types';
import { AVAILABLE_DEAL_STRUCTURE_STATUSES } from '../../consts/consts';
import { DefaultTooltip } from '../common/styles';

export const LoanVariablesModalHeader: FC<{
  showSuccessSubmit: boolean;
  uppercaseTitle: boolean;
  modalTitle: JSX.Element;
  handleUndo: () => Promise<void>;
  formHistory: any[];
  handleHideWithResetVehicle: () => void;
  handleViewWorksheet: () => void;
  isAdjustDealModalAnimated: boolean;
  isPendingRecalculation: boolean;
  isGeneratingWorksheet: boolean;
  isLender: boolean;
  availableDealStructureStatus: IAvailableDealStructureStatus;
  isAddendumActive: boolean;
}> = ({
  showSuccessSubmit,
  uppercaseTitle,
  modalTitle,
  handleUndo,
  formHistory,
  handleHideWithResetVehicle,
  handleViewWorksheet,
  isAdjustDealModalAnimated,
  isPendingRecalculation,
  isGeneratingWorksheet,
  isLender,
  availableDealStructureStatus,
  isAddendumActive,
}) => {
  const { t } = useTranslation();
  const disabledFeaturesList = useSelector(getDisabledFeaturesList);
  const user = useSelector(getUser);
  const isAllyLender = useIsAllyLender(isLender);
  const tooltipRef = useRef<TooltipRefProps>(null);

  const handleViewWorksheetClick = () => {
    handleViewWorksheet();
    setTimeout(() => tooltipRef.current?.close(), 2500);
  };

  return (
    <LoanVariablesModalHeaderWrapper closeButton={showSuccessSubmit} $showSuccessSubmit={showSuccessSubmit}>
      <Title $uppercase={uppercaseTitle}>{modalTitle}</Title>
      {!showSuccessSubmit && (
        <ButtonContainerBlock>
          {!disabledFeaturesList.includes(SUPPORTED_FEATURES_MAPPER.is_payment_option_active) && (
            <>
              {user?.hasPaymentOption && isLender && !isAllyLender && (
                <>
                  <ButtonWorksheetBlock
                    onClick={handleViewWorksheetClick}
                    data-tooltip-id="view-worksheet-tooltip"
                    disabled={
                      isPendingRecalculation ||
                      isAdjustDealModalAnimated ||
                      isAddendumActive ||
                      isGeneratingWorksheet ||
                      availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.MISSING_OR_INVALID_INFO
                    }
                  >
                    {isGeneratingWorksheet ? <ButtonSpinner /> : <ButtonViewWorksheet />}
                  </ButtonWorksheetBlock>
                  <DefaultTooltip ref={tooltipRef} id="view-worksheet-tooltip" place="bottom-start">
                    {isGeneratingWorksheet
                      ? t('components.loanVariablesModal.buttons.viewWorksheetGenerating')
                      : t('components.loanVariablesModal.buttons.viewWorksheet')}
                  </DefaultTooltip>
                </>
              )}
            </>
          )}
          <ButtonUndoBlock
            onClick={handleUndo}
            disabled={formHistory.length === 1 || isAdjustDealModalAnimated || isAddendumActive}
          >
            <ButtonUndoLabel>{t('components.loanVariablesModal.buttons.undo')}</ButtonUndoLabel>
            <ButtonUndo />
          </ButtonUndoBlock>
          <ModalCloseButton className="modal-close-button" onClick={() => handleHideWithResetVehicle()} />
        </ButtonContainerBlock>
      )}
    </LoanVariablesModalHeaderWrapper>
  );
};
